import * as L from 'leaflet'
import { CanvasShipUtils } from '@map-sdk/sdk/utils/CanvasShipUtils'
import type { MyMap } from '@map-sdk/sdk/ShipxyAPISDK'
import 'leaflet-graticule'

export interface OptionsLatLngGraticule extends L.GraticuleOptions {
  showLabel: boolean
  weight: number
  color1: string
  color2: string
  fontColor1: string
  fontColor2: string
  dashArray: [number, number]
  latFormatTickLabel: (value: number) => string
  lngFormatTickLabel: (value: number) => string
  zoomInterval: { start: number; end: number; interval: number }[]
}

class LatLngGraticuleServer {
  private _isView = false
  private _options: OptionsLatLngGraticule
  private _map: MyMap
  private _layer: L.Layer | undefined

  constructor(map: MyMap, options: Partial<OptionsLatLngGraticule> = {}) {
    this._options = {
      showLabel: true,
      weight: 0.8,
      color1: '#666',
      color2: '#bbb',
      fontColor1: '#333',
      fontColor2: '#fff',
      dashArray: [0, 0],
      latFormatTickLabel: CanvasShipUtils.latFormatter,
      lngFormatTickLabel: CanvasShipUtils.lngFormatter,
      zoomInterval: [
        { start: 2, end: 2, interval: 60 },
        { start: 3, end: 3, interval: 30 },
        { start: 4, end: 4, interval: 15 },
        { start: 5, end: 5, interval: 7.5 },
        { start: 6, end: 6, interval: 4 },
        { start: 7, end: 7, interval: 2 },
        { start: 8, end: 8, interval: 1 },
        { start: 9, end: 9, interval: 0.5 },
        { start: 10, end: 10, interval: 0.25 },
        { start: 11, end: 11, interval: 0.125 },
        { start: 12, end: 12, interval: 0.0625 },
        { start: 13, end: 13, interval: 0.033333333 },
        { start: 14, end: 14, interval: 0.016666666666 },
        { start: 15, end: 15, interval: 0.008333333 },
        { start: 16, end: 16, interval: 0.004166667 },
        { start: 17, end: 17, interval: 0.002083333 },
        { start: 18, end: 18, interval: 0.001041667 },
      ],
    }

    Object.assign(this._options, options)
    this._map = map
    this._map.on('baselayerchange', () => {
      this._hide()
      this._draw()
    })
  }

  public show(): this {
    this._isView = true
    this._hide()
    this._draw()
    return this
  }

  public hide(): this {
    this._isView = false
    this._hide()
    return this
  }

  private _hide(): this {
    if (this._layer) {
      this._map.removeLayer(this._layer)
      this._layer = undefined
    }
    return this
  }

  private _draw(): L.Layer | undefined {
    if (this._isView) {
      const overlayPane = this._map.getPane('overlayPane')
      if (overlayPane) {
        const isSatellite = overlayPane.dataset.mapType === 'MT_SATELLITE' // 假设 'MT_SATELLITE' 是卫星地图模式的标识符
        if (isSatellite) {
          this._options.color = this._options.color2 || this._options.color
          this._options.fontColor =
            this._options.fontColor2 || this._options.fontColor
        } else {
          this._options.color = this._options.color1 || this._options.color
          this._options.fontColor =
            this._options.fontColor1 || this._options.fontColor
        }

        // 假设 L.latlngGraticule 是一个存在并且能处理 options 参数的方法或工厂函数
        this._layer = L.latlngGraticule(this._options).addTo(this._map)
        return this._layer
      }
    }
  }
}

const latlngGraticuleServer = (
  map: MyMap,
  options?: Partial<OptionsLatLngGraticule> | undefined
) => new LatLngGraticuleServer(map, options)

export { LatLngGraticuleServer, latlngGraticuleServer }
